import { Editor } from '@tiptap/react';
import { useEffect, useRef,useState } from 'react';

import classes from './LinkPopup.module.scss';

interface LinkPopupProps {
	editor: Editor | null;
	isOpen: boolean;
	onClose: () => void;
	buttonRef: React.RefObject<HTMLDivElement | null>;
}

interface LinkData {
	url: string;
	text: string;
}

const LinkPopup = ({ editor, isOpen, onClose, buttonRef }: LinkPopupProps) => {
	const [linkData, setLinkData] = useState<LinkData>({ url: '', text: '' });
	const popupRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (isOpen) {
			const selectedText = editor?.state.selection.empty
				? ''
				: editor?.state.doc.textBetween(
						editor.state.selection.from,
						editor.state.selection.to
					) || '';

			setLinkData({
				url: editor?.isActive('link') ? editor.getAttributes('link').href : '',
				text: selectedText,
			});
		}
	}, [isOpen, editor]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				popupRef.current &&
				!popupRef.current.contains(event.target as Node) &&
				buttonRef.current &&
				!buttonRef.current.contains(event.target as Node)
			) {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen, onClose, buttonRef]);

	const handleInsertLink = () => {
		if (linkData.url.trim() && editor) {
			if (linkData.text && !editor.state.selection.empty) {
				editor
					.chain()
					.focus()
					.extendMarkRange('link')
					.setLink({ href: linkData.url })
					.run();
			} else {
				editor
					.chain()
					.focus()
					.insertContent(
						`<a href="${linkData.url}" target="_blank">${
							linkData.text || linkData.url
						}</a>`
					)
					.run();
			}
			onClose();
			setLinkData({ url: '', text: '' });
		}
	};

	const handleRemoveLink = () => {
		editor?.chain().focus().extendMarkRange('link').unsetLink().run();
		onClose();
	};

	if (!isOpen) return null;

	return (
		<div ref={popupRef} className={classes.linkPopup} onClick={(e) => e.stopPropagation()}>
			<div className={classes.popupContent}>
				<div className={classes.formGroup}>
					<label htmlFor='link-url'>Ссылка (URL):</label>
					<input
						id='link-url'
						type='url'
						value={linkData.url}
						onChange={(e) => setLinkData({ ...linkData, url: e.target.value })}
						placeholder='https://example.com'
						className={classes.input}
						autoFocus
					/>
				</div>
				<div className={classes.formGroup}>
					<label htmlFor='link-text'>Текст ссылки:</label>
					<input
						id='link-text'
						type='text'
						value={linkData.text}
						onChange={(e) => setLinkData({ ...linkData, text: e.target.value })}
						placeholder='Текст ссылки'
						className={classes.input}
					/>
				</div>
			</div>
			<div className={classes.popupActions}>
				<button
					type='button'
					className={`${classes.button} ${classes.buttonSecondary}`}
					onClick={onClose}
				>
					Отмена
				</button>
				{editor?.isActive('link') && (
					<button
						type='button'
						className={`${classes.button} ${classes.buttonDanger}`}
						onClick={handleRemoveLink}
					>
						Удалить
					</button>
				)}
				<button
					type='button'
					className={`${classes.button} ${classes.buttonPrimary}`}
					onClick={handleInsertLink}
					disabled={!linkData.url.trim()}
				>
					Вставить
				</button>
			</div>
		</div>
	);
};

export default LinkPopup;
